import React, { useEffect, useRef, useState } from 'react';
import { Sun, Wind, Battery, MapPin, X } from 'lucide-react';
import { STATS as FALLBACK_STATS, PROJECT_LOCATIONS as FALLBACK_PROJECTS } from '../mock/mock';

const useCountUp = (target, active) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    const raw = String(target);
    const hasPlus = raw.includes('+');
    const num = parseFloat(raw);
    if (isNaN(num)) { setN(raw); return; }
    let start = 0;
    const step = num / 40;
    const isDecimal = raw.includes('.');
    const t = setInterval(() => {
      start += step;
      if (start >= num) {
        setN(isDecimal ? num.toFixed(1) + (hasPlus ? '+' : '') : Math.round(num) + (hasPlus ? '+' : ''));
        clearInterval(t);
      } else {
        setN(isDecimal ? (Math.round(start * 10) / 10).toFixed(1) : Math.round(start).toString());
      }
    }, 30);
    return () => clearInterval(t);
  }, [target, active]);
  return n;
};

const StatItem = ({ stat, active }) => {
  const n = useCountUp(stat.value, active);
  return (
    <div className="group">
      <div className="text-4xl md:text-5xl font-bold text-white leading-none mb-2">
        {n}
        <span className="text-[#a3d859] ml-1">{stat.suffix}</span>
      </div>
      <div className="text-white/70 text-sm">{stat.label}</div>
    </div>
  );
};

const GreenSolutions = ({ stats, projects }) => {
  const STATS = stats && stats.length ? stats : FALLBACK_STATS;
  const PROJECT_LOCATIONS = projects && projects.length ? projects : FALLBACK_PROJECTS;
  const [selected, setSelected] = useState(null);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setVisible(true),
      { threshold: 0.2 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="relative py-24 overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #2d4a1a 0%, #3d6323 50%, #4a7a2b 100%)',
      }}
    >
      {/* Decorative pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(163,216,89,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(163,216,89,0.3) 0%, transparent 40%)'
        }} />
      </div>

      <div className="relative max-w-[1280px] mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Content */}
          <div>
            <span className="inline-block text-[#a3d859] text-xs font-bold tracking-[4px] mb-4">
              GREEN ENERGY SOLUTIONS
            </span>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-[1.15] mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Transforming the Nation with Green Energy Solutions
            </h2>
            <p className="text-white/80 leading-[1.9] mb-10 max-w-xl">
              We have established our presence as the leading renewable IPP green energy
              solutions company with our projects across India. With the aim of creating
              enduring value for our stakeholders and communities, we are making significant
              strides toward achieving carbon neutrality.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-8 mb-10">
              {STATS.map((s) => (
                <StatItem key={s.label} stat={s} active={visible} />
              ))}
            </div>

            {/* Icons */}
            <div className="flex gap-4">
              {[
                { Icon: Sun, label: 'Solar' },
                { Icon: Wind, label: 'Wind' },
                { Icon: Battery, label: 'BESS' },
              ].map(({ Icon, label }) => (
                <div key={label} className="group flex flex-col items-center gap-2 cursor-pointer">
                  <div className="w-14 h-14 rounded-full bg-white/10 border border-white/30 flex items-center justify-center group-hover:bg-[#a3d859] group-hover:border-[#a3d859] transition-all">
                    <Icon className="w-6 h-6 text-white group-hover:text-[#2d4a1a] transition-colors" strokeWidth={1.6} />
                  </div>
                  <span className="text-[11px] text-white/70 tracking-wider">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: India Map with pins */}
          <div className="relative flex justify-center">
            <div className="relative w-full max-w-[520px] aspect-[3/4]">
              {/* Stylized India shape (SVG) */}
              <svg
                viewBox="0 0 400 500"
                className="w-full h-full drop-shadow-2xl"
                fill="none"
              >
                <defs>
                  <linearGradient id="indiaG" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#a3d859" />
                    <stop offset="100%" stopColor="#5a8c26" />
                  </linearGradient>
                </defs>
                {/* Simplified India outline */}
                <path
                  d="M155 30 C170 22 195 22 215 28 C240 32 265 30 285 40 C300 48 315 60 320 78 C325 95 320 110 315 120 C325 128 335 138 340 155 C345 175 340 190 335 205 C332 220 322 235 320 250 C315 268 305 285 295 300 C285 315 275 330 265 348 C255 365 245 380 235 395 C225 410 215 425 205 435 C198 445 190 455 180 458 C170 460 160 452 155 442 C148 430 142 415 138 400 C132 385 128 370 122 355 C115 340 108 325 102 310 C95 295 88 280 82 260 C76 240 72 220 70 200 C68 180 72 160 78 145 C85 128 92 115 100 100 C110 82 122 65 135 50 C142 42 148 35 155 30 Z"
                  fill="url(#indiaG)"
                  stroke="#a3d859"
                  strokeWidth="1.5"
                  opacity="0.95"
                />
                {/* Kashmir top */}
                <path
                  d="M155 30 C160 20 175 18 190 25 C185 15 200 8 215 15 C220 20 218 28 210 30"
                  fill="url(#indiaG)"
                  stroke="#a3d859"
                  strokeWidth="1"
                  opacity="0.9"
                />
                {/* North-east arm */}
                <path
                  d="M310 100 C325 95 340 92 350 100 C360 108 358 122 350 130 C355 140 355 155 345 160 C335 158 325 150 320 140"
                  fill="url(#indiaG)"
                  stroke="#a3d859"
                  strokeWidth="1"
                  opacity="0.9"
                />
                {/* State texture */}
                <g stroke="#fff" strokeWidth="0.4" opacity="0.2" fill="none">
                  <path d="M120 100 L200 130" />
                  <path d="M160 180 L280 200" />
                  <path d="M140 240 L300 260" />
                  <path d="M180 320 L280 340" />
                </g>
              </svg>

              {/* Pins */}
              {PROJECT_LOCATIONS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setSelected(p)}
                  className="absolute -translate-x-1/2 -translate-y-full group"
                  style={{ top: p.top, left: p.left }}
                >
                  <span className="relative flex">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-60 animate-ping" />
                    <MapPin className="w-6 h-6 text-white fill-[#E10D0D] drop-shadow-lg group-hover:scale-125 transition-transform" />
                  </span>
                  <span className="absolute left-1/2 -translate-x-1/2 mt-1 whitespace-nowrap bg-white text-[#2d4a1a] text-[10px] font-bold px-2 py-1 rounded shadow opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    {p.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modal for project detail */}
      {selected && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4 animate-in fade-in duration-200"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white rounded-lg max-w-md w-full p-8 relative shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelected(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-800"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="w-14 h-14 rounded-full bg-[#8ec045]/15 flex items-center justify-center mb-4">
              <Sun className="w-7 h-7 text-[#8ec045]" />
            </div>
            <h3 className="text-2xl font-bold text-[#1a1a1a] mb-2" style={{fontFamily:"'Playfair Display', serif"}}>
              {selected.name}
            </h3>
            <p className="text-[#666] mb-4">Located in {selected.state}, this large-scale solar project contributes to India\u2019s green energy transition.</p>
            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-gray-100">
              <div><div className="text-xs text-gray-500">Capacity</div><div className="font-bold text-[#2d4a1a]">{selected.capacity || '250 MWp'}</div></div>
              <div><div className="text-xs text-gray-500">Status</div><div className="font-bold text-[#2d4a1a]">{selected.status || 'Operational'}</div></div>
              <div><div className="text-xs text-gray-500">Households</div><div className="font-bold text-[#2d4a1a]">{selected.households || '125K+'}</div></div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default GreenSolutions;
